import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Globe,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Info,
  Loader2,
  Eye,
  EyeOff,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { api } from '../../../context/AuthContext';
import { toast } from 'sonner';
import PlatformIcons from '../../../components/icons/PlatformIcons';

const PLATFORMS = [
  { id: 'wordpress',   name: 'WordPress',          Icon: PlatformIcons.wordpress, authType: 'credentials' },
  { id: 'shopify',     name: 'Shopify',            Icon: PlatformIcons.shopify,   authType: 'oauth' },
  { id: 'wix',         name: 'Wix',                Icon: PlatformIcons.wix,       authType: 'oauth' },
  { id: 'squarespace', name: 'Squarespace',        Icon: PlatformIcons.squarespace, authType: 'credentials' },
];

const CREDENTIAL_FIELDS = {
  wordpress: {
    description: 'Enter your WordPress site URL and an Application Password. This lets GDS read your site data without your main password.',
    fields: [
      { key: 'site_url', label: 'WordPress Site URL', placeholder: 'https://yoursite.com', type: 'url', isUrl: true },
      { key: 'username', label: 'WordPress Username', placeholder: 'admin', type: 'text' },
      { key: 'app_password', label: 'Application Password', placeholder: 'xxxx xxxx xxxx xxxx', type: 'password' },
    ],
    helpSteps: [
      'Log in to your WordPress admin panel',
      'Go to Users -> Your Profile',
      'Scroll to "Application Passwords"',
      'Enter a name (e.g. "GDS SEO") and click "Add New Application Password"',
      'Copy the generated password and paste it above',
    ],
  },
  squarespace: {
    description: 'Enter your Squarespace site URL and an API Key generated from your site\'s Developer settings.',
    fields: [
      { key: 'site_url', label: 'Squarespace Site URL', placeholder: 'https://yoursite.com', type: 'url', isUrl: true },
      { key: 'api_key', label: 'API Key', placeholder: 'Your Squarespace API key', type: 'password' },
    ],
    helpSteps: [
      'Log in to your Squarespace site',
      'Go to Settings -> Developer -> API Keys',
      'Click "Generate API Key"',
      'Copy the key and paste it above',
    ],
  },
};

const OAUTH_PLATFORMS = {
  shopify: {
    description: 'Click the button below to connect your Shopify store. You\'ll be redirected to Shopify to authorize GDS.',
    needsShopUrl: true,
    shopLabel: 'Your Shopify store URL',
    shopPlaceholder: 'my-store.myshopify.com',
    helpSteps: [
      'Enter your Shopify store domain below',
      'Click "Connect Shopify"',
      'You\'ll be redirected to Shopify to authorize GDS',
      'Grant the requested permissions to read and update store content',
      'You\'ll be redirected back here automatically',
    ],
  },
  wix: {
    description: 'Click the button below to connect your Wix site. You\'ll be redirected to Wix to authorize GDS.',
    needsShopUrl: false,
    helpSteps: [
      'Click "Connect Wix" below',
      'You\'ll be redirected to Wix to install the GDS app',
      'Authorize the requested permissions',
      'You\'ll be redirected back here automatically',
    ],
  },
};

export default function WebsiteAccessStep({ data, onNext, onBack }) {
  const [selectedPlatform, setSelectedPlatform] = useState(data?.platform || '');
  const [credentials, setCredentials] = useState({});
  const [notes, setNotes] = useState(data?.notes || '');
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [showHelp, setShowHelp] = useState(false);
  const [shopUrl, setShopUrl] = useState('');

  const platformInfo = PLATFORMS.find(p => p.id === selectedPlatform);
  const isOAuth = platformInfo?.authType === 'oauth';
  const isCredentials = platformInfo?.authType === 'credentials';
  const credConfig = CREDENTIAL_FIELDS[selectedPlatform];
  const oauthConfig = OAUTH_PLATFORMS[selectedPlatform];
  const isConnected = connectionStatus === 'connected';

  useEffect(() => {
    // After OnboardingCallback processes a Shopify/Wix OAuth it redirects back
    // to /onboarding?cms_connected=<platform> so we can mark the step as done.
    const params = new URLSearchParams(window.location.search);
    const cmsConnected = params.get('cms_connected');

    if (cmsConnected) {
      setSelectedPlatform(cmsConnected);
      setConnectionStatus('connected');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlatformChange = (value) => {
    setSelectedPlatform(value);
    setCredentials({});
    setConnectionStatus(null);
    setShowHelp(false);
    setShopUrl('');
  };

  const handleFieldChange = (key, value) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
    if (connectionStatus) setConnectionStatus(null);
  };

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // --- Direct credentials: WordPress / Squarespace ---
  const handleTestConnection = async () => {
    if (!selectedPlatform || !credConfig) return;

    const missingFields = credConfig.fields.filter(f => !credentials[f.key]?.trim());
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setLoading(true);
    setConnectionStatus('testing');
    try {
      const siteUrl = credentials.site_url;
      const creds = { ...credentials };
      delete creds.site_url;

      await api.post('/cms/connect', {
        platform: selectedPlatform,
        site_url: siteUrl,
        credentials: creds,
      });
      setConnectionStatus('connected');
      toast.success('CMS connected successfully!');
    } catch (err) {
      setConnectionStatus('failed');
      toast.error(err.response?.data?.detail || 'Connection failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  // --- OAuth redirect: Shopify / Wix ---
  const handleOAuthRedirect = async () => {
    if (selectedPlatform === 'shopify' && !shopUrl.trim()) {
      toast.error('Please enter your Shopify store URL');
      return;
    }

    setLoading(true);
    try {
      const redirectUri = `${window.location.origin}/onboarding/callback`;
      // Tell OnboardingCallback which platform is being connected
      sessionStorage.setItem('cms_oauth_platform', selectedPlatform);

      let resp;
      if (selectedPlatform === 'shopify') {
        resp = await api.get('/cms/shopify/auth-url', {
          params: { shop: shopUrl.trim(), redirect_uri: redirectUri },
        });
      } else if (selectedPlatform === 'wix') {
        resp = await api.get('/cms/wix/auth-url', {
          params: { redirect_uri: redirectUri },
        });
      }

      if (resp?.data?.auth_url) {
        window.location.href = resp.data.auth_url;
      }
    } catch (err) {
      sessionStorage.removeItem('cms_oauth_platform');
      toast.error(err.response?.data?.detail || 'Failed to start OAuth flow');
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!selectedPlatform) {
      toast.error('Please select your website platform');
      return;
    }

    if (!isConnected) {
      toast.error('Please connect your CMS first');
      return;
    }

    setLoading(true);
    try {
      await api.post('/onboarding/website-access', {
        platform: selectedPlatform,
        access_shared: true,
        notes: notes,
      });
      toast.success('Website access information saved');
      onNext();
    } catch {
      toast.error('Failed to save website access');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-indigo-100 flex items-center justify-center mx-auto mb-4">
          <Globe className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold font-heading mb-2">Connect Your Website</h1>
        <p className="text-slate-600 max-w-lg mx-auto">
          Connect your CMS so we can analyze your site content, identify SEO opportunities, and track improvements.
        </p>
      </div>

      {/* Platform Selection */}
      <Card className="gds-card">
        <CardHeader>
          <CardTitle>Which platform is your website built on?</CardTitle>
          <CardDescription>Select the platform that powers your website</CardDescription>
        </CardHeader>
        <CardContent>
          <RadioGroup value={selectedPlatform} onValueChange={handlePlatformChange} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PLATFORMS.map((platform) => {
              const PIcon = platform.Icon;
              return (
                <div key={platform.id}>
                  <RadioGroupItem value={platform.id} id={platform.id} className="peer sr-only" />
                  <Label
                    htmlFor={platform.id}
                    className="flex flex-col items-center justify-center rounded-xl border-2 border-slate-200 bg-white p-4 hover:bg-slate-50 peer-data-[state=checked]:border-indigo-600 peer-data-[state=checked]:bg-indigo-50 cursor-pointer transition-all"
                  >
                    <PIcon className="w-7 h-7 mb-2" />
                    <span className="text-sm font-medium">{platform.name}</span>
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </CardContent>
      </Card>

      {/* OAuth flow: Shopify / Wix */}
      {isOAuth && oauthConfig && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Connect to {platformInfo.name}
              </CardTitle>
              <CardDescription>{oauthConfig.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {oauthConfig.needsShopUrl && (
                <div>
                  <Label htmlFor="shop-url">{oauthConfig.shopLabel}</Label>
                  <Input
                    id="shop-url"
                    type="text"
                    placeholder={oauthConfig.shopPlaceholder}
                    value={shopUrl}
                    onChange={(e) => setShopUrl(e.target.value)}
                    disabled={isConnected}
                    className="mt-1"
                  />
                </div>
              )}

              {/* Connection Status */}
              {connectionStatus && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  connectionStatus === 'connected' ? 'bg-emerald-50 text-emerald-700' :
                  connectionStatus === 'failed' ? 'bg-red-50 text-red-700' :
                  'bg-sky-50 text-sky-700'
                }`}>
                  {connectionStatus === 'connected' && <CheckCircle2 className="w-4 h-4" />}
                  {connectionStatus === 'failed' && <XCircle className="w-4 h-4" />}
                  {connectionStatus === 'testing' && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>
                    {connectionStatus === 'connected' && `${platformInfo.name} connected successfully!`}
                    {connectionStatus === 'failed' && 'Connection failed. Please try again.'}
                    {connectionStatus === 'testing' && 'Verifying connection...'}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button
                  onClick={handleOAuthRedirect}
                  disabled={loading || isConnected}
                  className={isConnected ? 'bg-emerald-600 hover:bg-emerald-700' : 'gds-btn-primary'}
                >
                  {loading && connectionStatus === 'testing' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
                  ) : isConnected ? (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Connected</>
                  ) : (
                    <><ExternalLink className="w-4 h-4 mr-2" /> Connect {platformInfo.name}</>
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowHelp(!showHelp)}>
                  <Info className="w-4 h-4 mr-1" />
                  {showHelp ? 'Hide' : 'Show'} Guide
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Credential form: WordPress / Squarespace */}
      {isCredentials && credConfig && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-indigo-600" />
                Connect to {platformInfo.name}
              </CardTitle>
              <CardDescription>{credConfig.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {credConfig.fields.map((field) => (
                <div key={field.key}>
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <div className="relative mt-1">
                    <Input
                      id={field.key}
                      type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                      placeholder={field.placeholder}
                      value={credentials[field.key] || ''}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      disabled={isConnected}
                    />
                    {field.type === 'password' && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => togglePasswordVisibility(field.key)}
                      >
                        {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Connection Status */}
              {connectionStatus && (
                <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  connectionStatus === 'connected' ? 'bg-emerald-50 text-emerald-700' :
                  connectionStatus === 'failed' ? 'bg-red-50 text-red-700' :
                  'bg-sky-50 text-sky-700'
                }`}>
                  {connectionStatus === 'connected' && <CheckCircle2 className="w-4 h-4" />}
                  {connectionStatus === 'failed' && <XCircle className="w-4 h-4" />}
                  {connectionStatus === 'testing' && <Loader2 className="w-4 h-4 animate-spin" />}
                  <span>
                    {connectionStatus === 'connected' && 'Connected successfully! Your CMS is verified.'}
                    {connectionStatus === 'failed' && 'Connection failed. Please check your credentials and try again.'}
                    {connectionStatus === 'testing' && 'Testing connection...'}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button
                  onClick={handleTestConnection}
                  disabled={loading || isConnected}
                  className={isConnected ? 'bg-emerald-600 hover:bg-emerald-700' : 'gds-btn-primary'}
                >
                  {loading && connectionStatus === 'testing' ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                  ) : isConnected ? (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Connected</>
                  ) : (
                    'Test Connection'
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowHelp(!showHelp)}>
                  <Info className="w-4 h-4 mr-1" />
                  {showHelp ? 'Hide' : 'Show'} Setup Guide
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Help Steps (shown for both credential and OAuth platforms) */}
      {showHelp && (credConfig || oauthConfig) && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="gds-card border-indigo-200 bg-indigo-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-indigo-600" />
                How to connect {platformInfo?.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(credConfig?.helpSteps || oauthConfig?.helpSteps || []).map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-indigo-600 text-white text-sm flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="text-slate-700">{step}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-200">
                <h4 className="font-medium mb-2">Why we need access:</h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>- Analyze your page content and meta tags for SEO gaps</li>
                  <li>- Identify missing meta titles and descriptions</li>
                  <li>- Check for schema markup across your site</li>
                  <li>- Track content changes over time</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!selectedPlatform || !isConnected || loading}
          className="gds-btn-primary"
        >
          {loading ? (
            <span className="flex items-center">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </span>
          ) : (
            <>
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
